// GSAP is already available globally through WordPress enqueue
class PropertyAnimations {
    constructor() {
        this.propertyList = document.querySelector('.c-row_property_list');
        this.activeScrollTriggers = [];
        
        // Wait for DOM to be ready before initializing
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.init());
        } else {
            this.init();
        }

        // Listen for property updates from AJAX
        document.addEventListener('propertyUpdate', (event) => {
            this.cleanupScrollTriggers();
            this.animateInitialItems();
            // Refresh ScrollTrigger to recognize new content after filtering
            if (typeof ScrollTrigger !== 'undefined') {
                ScrollTrigger.refresh();
                // Force animate items currently in viewport
                this.animateVisibleItems();
            }
        });
        
        // Listen for property load more updates
        document.addEventListener('propertyLoadMore', (event) => {
            // Refresh ScrollTrigger to recognize new content
            if (typeof ScrollTrigger !== 'undefined') {
                ScrollTrigger.refresh();
            }
        });
        
        // Listen for filter close events
        document.addEventListener('filtersToggled', (event) => {
            if (typeof ScrollTrigger !== 'undefined') {
                // Allow layout to update first
                setTimeout(() => {
                    // Refresh ScrollTrigger and animate visible items
                    ScrollTrigger.refresh();
                    this.animateVisibleItems();
                }, 50);
            }
        });
    }

    init() {
        // Wait for page transition to complete
        if (document.body.classList.contains('page-transitioning')) {
            document.addEventListener('pageTransitionComplete', () => {
                this.initExistingAnimations();
                this.initMutationObserver();
            });
        } else {
            this.initExistingAnimations();
            this.initMutationObserver();
        }
    }

    animateInitialItems() {
        const items = Array.from(this.propertyList.querySelectorAll('.c-col_property_item'));
        if (items.length > 0) {
            this.animatePropertyItems(items);
        }
    }

    initExistingAnimations() {
        const existingItems = Array.from(this.propertyList.querySelectorAll('.c-col_property_item'));
        if (existingItems.length > 0) {
            this.animatePropertyItems(existingItems);
        }
    }

    initMutationObserver() {
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                    const newItems = Array.from(mutation.addedNodes).filter(node => 
                        node.classList && node.classList.contains('c-col_property_item')
                    );
                    if (newItems.length > 0) {
                        this.animatePropertyItems(newItems);
                    }
                }
            });
        });

        observer.observe(this.propertyList, {
            childList: true,
            subtree: true
        });
    }

    cleanupScrollTriggers() {
        this.activeScrollTriggers.forEach(trigger => {
            trigger.kill();
        });
        this.activeScrollTriggers = [];
    }

    animatePropertyItems(items) {
        items.forEach((element) => {
            if (!element) return; // Skip if element is null
            
            const $image = element.querySelector('.property-image');
            const $text = element.querySelector('.property-text');
            const $tag = element.querySelector('.property-tag');
            
            // Skip animation if required elements don't exist
            if (!$image || !$text) {
                console.warn('Required elements not found for property animation:', element);
                return;
            }
            
            // Set initial states
            gsap.set(element, { 
                opacity: 0
            });
            gsap.set($image, { 
                scale: 1.07
            });
            if ($tag) {
                gsap.set($tag, { 
                    opacity: 0,
                    y: 5
                });
            }
            
            const trigger = ScrollTrigger.create({
                trigger: element,
                start: "top bottom-=100",
                onEnter: () => {
                    const tl = gsap.timeline({
                        onComplete: () => {
                            element.classList.add('loaded');
                            setTimeout(() => {
                                // Clear all GSAP properties
                                gsap.set(element, { clearProps: "all" });
                                gsap.set($image, { clearProps: "all" });
                                if ($tag) {
                                    gsap.set($tag, { clearProps: "all" });
                                }
                            }, 100)
                        }
                    });
                    
                    // Animate image and text together
                    tl.to(element, {
                        opacity: 1,
                        duration: 2,
                        ease: 'power3.out'
                    })
                    .to($image, {
                        scale: 1,
                        duration: 2.5,
                        ease: 'power4.out'
                    }, '<')
                    
                    // Animate tag if it exists
                    if ($tag) {
                        tl.to($tag, {
                            opacity: 1,
                            y: 0,
                            duration: 1.5,
                            ease: 'power2.out'
                        }, '<15%');
                    }
                }
            });

            // Store the ScrollTrigger instance for cleanup
            this.activeScrollTriggers.push(trigger);
        });
    }

    // Add a method to check and animate items currently visible in the viewport
    animateVisibleItems() {
        const items = Array.from(this.propertyList.querySelectorAll('.c-col_property_item:not(.loaded)'));
        
        items.forEach(item => {
            const rect = item.getBoundingClientRect();
            const isInViewport = (
                rect.top < window.innerHeight &&
                rect.bottom > 0
            );
            
            if (isInViewport) {
                // Find the ScrollTrigger for this item and manually trigger it
                this.activeScrollTriggers.forEach(trigger => {
                    if (trigger.vars.trigger === item) {
                        trigger.vars.onEnter();
                    }
                });
            }
        });
    }
}

export default PropertyAnimations; 